import { useState, type FormEvent } from 'react';
import { Modal } from '../../components/Modal';

/** Mirrors the backend's `MAX_FLAG_REASON_LENGTH`. */
const REASON_MAX_LENGTH = 2000;

interface FlagDialogProps {
  /** What is being reported, for the dialog's own wording. */
  subject: string;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (reason: string) => void;
  onDismiss: () => void;
}

/**
 * The reason a flag carries.
 *
 * A report goes to Autopostmate staff, not to a social platform and not to a
 * moderation queue that could take the draft away — so the copy says so, and
 * nothing on the review screen is disabled while this is open. The user can
 * close it and approve the very draft they were reporting.
 */
export function FlagDialog({ subject, isSubmitting, error, onSubmit, onDismiss }: FlagDialogProps) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const isValid = trimmed.length > 0 && trimmed.length <= REASON_MAX_LENGTH;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid || isSubmitting) return;
    onSubmit(trimmed);
  }

  return (
    <Modal title={`Report ${subject}`} kind="report" onDismiss={onDismiss}>
      <p className="modal__message">
        This goes to Autopostmate staff for review. It is not sent to a social platform, and nothing
        is published, deleted or regenerated by reporting it — you can still approve, reject or use
        this draft afterwards.
      </p>

      <form className="flag-form" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field__label">What is wrong with it</span>
          <textarea
            className="field__input"
            rows={5}
            maxLength={REASON_MAX_LENGTH}
            autoFocus
            value={reason}
            disabled={isSubmitting}
            onChange={(event) => setReason(event.target.value)}
          />
          <span className="field__hint">
            Required — {reason.length} of {REASON_MAX_LENGTH} characters. A snapshot of what you are
            looking at is attached automatically.
          </span>
        </label>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="modal__actions">
          <button className="button" type="button" onClick={onDismiss} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            className="button button--danger"
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? 'Sending' : 'Send report'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
